/**
 * Zugriff auf die eigene Schnittstelle. Ein Weg fuer alle Aufrufe, damit
 * Fehlerbehandlung und Abmeldung bei abgelaufener Sitzung an einer Stelle
 * stehen und nicht in jeder Ansicht neu geschrieben werden.
 */

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    override readonly message: string,
    readonly feld?: string,
  ) {
    super(message)
    this.name = 'ApiFehler'
  }
}

type Verfahren = 'GET' | 'POST' | 'PATCH' | 'DELETE'

/**
 * Laeuft die Sitzung waehrend der Arbeit ab, antwortet jede Abfrage mit 401.
 * Ohne diesen Rueckruf bliebe die Oberflaeche stehen und zeigte in jeder
 * Ansicht "Nicht angemeldet", waehrend der Benutzer weiterklickt. Stattdessen
 * wird die Anmeldelage einmal zurueckgesetzt und die Anmeldung gezeigt.
 */
let beiSitzungsende: (() => void) | null = null

export function sitzungsendeMelden(rueckruf: (() => void) | null): void {
  beiSitzungsende = rueckruf
}

/** Die Anmeldung selbst antwortet bei falschem Passwort ebenfalls mit 401. */
const ANMELDEPFAD = '/api/anmeldung'

async function anfragen<T>(verfahren: Verfahren, pfad: string, koerper?: unknown): Promise<T> {
  const optionen: RequestInit = { method: verfahren, credentials: 'same-origin' }
  if (koerper instanceof Blob) {
    // Eine Datei wie die Sprachaufnahme geht roh, mit ihrem eigenen Format.
    optionen.headers = { 'content-type': koerper.type || 'application/octet-stream' }
    optionen.body = koerper
  } else if (koerper !== undefined) {
    optionen.headers = { 'content-type': 'application/json' }
    optionen.body = JSON.stringify(koerper)
  }

  let antwort: Response
  try {
    antwort = await fetch(pfad, optionen)
  } catch {
    throw new ApiFehler(0, 'Der Server ist nicht erreichbar.')
  }

  if (antwort.status === 204) return undefined as T

  const inhalt = antwort.headers.get('content-type') ?? ''
  const daten: unknown = inhalt.includes('application/json') ? await antwort.json() : await antwort.text()

  if (!antwort.ok) fehlerWerfen(antwort.status, daten, pfad)

  return daten as T
}

function fehlerWerfen(status: number, daten: unknown, pfad: string): never {
  const nachricht =
    typeof daten === 'object' && daten !== null && 'fehler' in daten && typeof daten.fehler === 'string'
      ? daten.fehler
      : 'Es ist ein Fehler aufgetreten.'
  const feld =
    typeof daten === 'object' && daten !== null && 'feld' in daten && typeof daten.feld === 'string'
      ? daten.feld
      : undefined
  if (status === 401 && pfad !== ANMELDEPFAD) beiSitzungsende?.()

  throw new ApiFehler(status, nachricht, feld)
}

/**
 * Eine Datei vom Server herunterladen, etwa die Analyse als PDF. Geholt wird
 * sie wie jede Abfrage, damit ein Fehler als Meldung in der Ansicht landet
 * und nicht als nackte Fehlerseite im Browser. Der Dateiname kommt vom Server.
 */
async function herunterladen(pfad: string): Promise<void> {
  let antwort: Response
  try {
    antwort = await fetch(pfad, { credentials: 'same-origin' })
  } catch {
    throw new ApiFehler(0, 'Der Server ist nicht erreichbar.')
  }
  if (!antwort.ok) {
    const art = antwort.headers.get('content-type') ?? ''
    fehlerWerfen(antwort.status, art.includes('application/json') ? await antwort.json() : null, pfad)
  }

  const kopf = antwort.headers.get('content-disposition') ?? ''
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(kopf)?.[1]
  const einfach = /filename="([^"]+)"/i.exec(kopf)?.[1]
  const name = utf8 ? decodeURIComponent(utf8) : (einfach ?? 'Download')

  const adresse = URL.createObjectURL(await antwort.blob())
  const verweis = document.createElement('a')
  verweis.href = adresse
  verweis.download = name
  document.body.append(verweis)
  verweis.click()
  verweis.remove()
  // Erst spaeter freigeben: manche Browser beginnen den Download verzoegert.
  setTimeout(() => URL.revokeObjectURL(adresse), 60_000)
}

export const api = {
  holen: <T>(pfad: string) => anfragen<T>('GET', pfad),
  senden: <T>(pfad: string, koerper?: unknown) => anfragen<T>('POST', pfad, koerper),
  aendern: <T>(pfad: string, koerper: unknown) => anfragen<T>('PATCH', pfad, koerper),
  loeschen: <T>(pfad: string) => anfragen<T>('DELETE', pfad),
  herunterladen,
}

/* ─────────────────────────────────────────── Gestalten der Schnittstelle */

export type Rolle = 'verwaltung' | 'kanzlei'
export type Werkzeug = 'bwa' | 'jahresabschluss'
export type Stand = 'offen' | 'analysiert' | 'versendet'
export type Einlesung = 'uebernommen' | 'abgelehnt' | null

/** Lesbare Namen fuer das, was in einer Datei steckt (dokument.enthaelt). */
export const ENTHAELT_NAME: Record<string, string> = {
  bwa: 'BWA',
  susa: 'Summen und Salden',
  jahresabschluss: 'Jahresabschluss',
  kontoblatt: 'Kontoblatt',
  kostenstellen: 'Kostenstellen',
  wertenachweis: 'Wertenachweis',
  handelsbilanz: 'Handelsbilanz',
  steuerbilanz: 'Steuerbilanz',
  ifrs: 'IFRS',
}

/**
 * Was in der Liste als Stand erscheint. Solange ein BWA Paket noch keine
 * Analyse hat, sagt der Stand, ob seine Zahlen im Bestand sind.
 */
export function standAnzeige(stand: Stand, einlesung: Einlesung | undefined): string {
  if (stand !== 'offen') return stand
  if (einlesung === 'uebernommen') return 'eingelesen'
  if (einlesung === 'abgelehnt') return 'abgelehnt'
  return stand
}

export interface Benutzer {
  id: string
  email: string
  name: string
  rolle: Rolle
}

export interface Sitzungsauskunft {
  benutzer: Benutzer
  kanzlei: string
  werkzeuge: Werkzeug[]
}

export interface MandantKurz {
  id: string
  beraternummer: number
  mandantennummer: number
  name: string
  rechtsform: string | null
  kontenrahmen: string | null
  aktiv: boolean
}

export interface Mandant extends MandantKurz {
  datev_mdt_id: number | null
  datev_stammdaten_guid: string | null
  datev_buha_id: string | null
  firmenname: string | null
  rechtsform_id: string | null
  ust_id: string | null
  mandant_seit: string | null
  kontenlaenge: number | null
  kostenlaenge: number | null
  waehrung: string | null
  versteuerung: string | null
  ustva_zeitraum: string | null
  wirtschaftsjahr_beginn: string | null
  jahre_verfuegbar: number | null
  stammdaten_stand: string | null
  notiz: string | null
}
